import type {
	BulkUpdateCustomFieldResponseDto,
	CreateCustomFieldDto,
	CreateCustomFieldResponseDto,
	CustomFieldDefinitionDto,
	CustomFieldEntityType,
	DeleteCustomFieldResponseDto,
	GetCustomFieldResponseDto,
	GetCustomFieldsResponseDto,
	UpdateCustomFieldDto,
	UpdateCustomFieldResponseDto,
} from '@supply-mover-2-0/shared';
import type { FilterConfig } from '../filters/config';
import { ApiService } from './ApiService';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class CustomFieldQueryService {
	/**
	 * Get all custom field definitions for an entity type
	 */
	public static async getFieldDefinitions(
		entityType?: CustomFieldEntityType,
		options?: { includeInactive?: boolean; visibleOnly?: boolean },
	): Promise<{
		data: Array<CustomFieldDefinitionDto>;
		success: boolean;
		error?: string;
	}> {
		try {
			const urlParams = new URLSearchParams();

			if (entityType != null) {
				urlParams.append('entityType', entityType);
			}

			if (options?.includeInactive === true) {
				urlParams.append('includeInactive', 'true');
			}

			if (options?.visibleOnly === true) {
				urlParams.append('visibleOnly', 'true');
			}

			const queryString = urlParams.toString();
			const url =
				queryString !== '' ? `/custom-fields?${queryString}` : '/custom-fields';

			const response = await ApiService.apiGet<GetCustomFieldsResponseDto>(url);

			if (response.success && response.data != null) {
				return {
					data: response.data,
					success: true,
				};
			}

			return {
				data: [],
				error:
					(response.success ? undefined : response.error) ??
					'Failed to fetch custom field definitions',
				success: false,
			};
		} catch (error) {
			console.error('Error fetching custom field definitions:', error);

			return {
				data: [],
				error: 'Failed to fetch custom field definitions',
				success: false,
			};
		}
	}

	/**
	 * Get a single custom field definition by ID
	 */
	public static async getFieldDefinition(id: number): Promise<{
		data: CustomFieldDefinitionDto | null;
		success: boolean;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<GetCustomFieldResponseDto>(
				`/custom-fields/${id}`,
			);

			if (response.success && response.data != null) {
				return {
					data: response.data,
					success: true,
				};
			}

			return {
				data: null,
				error:
					(response.success ? undefined : response.error) ??
					'Failed to fetch custom field definition',
				success: false,
			};
		} catch (error) {
			console.error('Error fetching custom field definition:', error);

			return {
				data: null,
				error: 'Failed to fetch custom field definition',
				success: false,
			};
		}
	}

	/**
	 * Create a new custom field definition
	 */
	public static async createFieldDefinition(
		dto: CreateCustomFieldDto,
	): Promise<{
		data: CustomFieldDefinitionDto | null;
		success: boolean;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiPost<CreateCustomFieldResponseDto>(
				'/custom-fields',
				dto,
			);

			if (response.success && response.data != null) {
				return {
					data: response.data,
					success: true,
				};
			}

			return {
				data: null,
				error:
					(response.success ? undefined : response.error) ??
					'Failed to create custom field definition',
				success: false,
			};
		} catch (error) {
			console.error('Error creating custom field definition:', error);

			return {
				data: null,
				error: 'Failed to create custom field definition',
				success: false,
			};
		}
	}

	/**
	 * Update an existing custom field definition
	 */
	public static async updateFieldDefinition(
		id: number,
		dto: UpdateCustomFieldDto,
	): Promise<{
		data: CustomFieldDefinitionDto | null;
		success: boolean;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiPut<UpdateCustomFieldResponseDto>(
				`/custom-fields/${id}`,
				dto,
			);

			if (response.success && response.data != null) {
				return {
					data: response.data,
					success: true,
				};
			}

			return {
				data: null,
				error:
					(response.success ? undefined : response.error) ??
					'Failed to update custom field definition',
				success: false,
			};
		} catch (error) {
			console.error('Error updating custom field definition:', error);

			return {
				data: null,
				error: 'Failed to update custom field definition',
				success: false,
			};
		}
	}

	/**
	 * Delete a custom field definition (and drop the physical column)
	 */
	public static async deleteFieldDefinition(id: number): Promise<{
		success: boolean;
		message?: string;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiDelete<DeleteCustomFieldResponseDto>(
				`/custom-fields/${id}`,
			);

			if (response.success) {
				return {
					success: true,
					message: response.message ?? 'Custom field deleted successfully',
				};
			}

			return {
				error: response.error ?? 'Failed to delete custom field definition',
				success: false,
			};
		} catch (error) {
			console.error('Error deleting custom field definition:', error);

			return {
				error: 'Failed to delete custom field definition',
				success: false,
			};
		}
	}

	/**
	 * Start a bulk update of a custom field value across multiple entities
	 */
	public static async startBulkUpdate(
		entityType: CustomFieldEntityType,
		entityIds: Array<number>,
		fieldKey: string,
		value: unknown,
	): Promise<{
		success: boolean;
		bulkActionId?: number;
		error?: string;
	}> {
		try {
			const response =
				await ApiService.apiPost<BulkUpdateCustomFieldResponseDto>(
					'/custom-fields/bulk/update',
					{
						entityType,
						entityIds,
						fieldKey,
						value,
					},
				);

			if (response.success && response.bulkActionId != null) {
				return {
					success: true,
					bulkActionId: response.bulkActionId,
				};
			}

			return {
				error:
					(response.success ? undefined : response.error) ??
					'Failed to start bulk update',
				success: false,
			};
		} catch (error) {
			console.error('Error starting bulk custom field update:', error);

			return {
				error: 'Failed to start bulk update',
				success: false,
			};
		}
	}

	/**
	 * Fetch synthesized FilterConfig entries for active custom fields on the
	 * given entity type. Returned map is keyed `cf:<fieldKey>` and can be merged
	 * into FILTER_REGISTRY surfaces (admin filter picker, runtime list filter
	 * rendering) so custom fields are usable as filters end-to-end.
	 */
	public static async getFilterConfigs(
		entityType: CustomFieldEntityType,
	): Promise<{
		data: Record<string, FilterConfig>;
		success: boolean;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data: Record<string, FilterConfig>;
			}>(`/custom-fields/filter-configs?entityType=${entityType}`);

			if (response?.success === true && response.data != null) {
				return { data: response.data, success: true };
			}

			return {
				data: {},
				success: false,
				error: 'Failed to fetch custom field filter configs',
			};
		} catch (error) {
			console.error('Error fetching custom field filter configs:', error);
			return {
				data: {},
				success: false,
				error:
					error instanceof Error
						? error.message
						: 'Failed to fetch custom field filter configs',
			};
		}
	}
}
